import { genericMemo, useFieldValue, useTranslate } from "ra-core";
import type { AnchorHTMLAttributes } from "react";
import React from "react";

import { cn } from "@/lib/utils";
import type { FieldProps } from "@/lib/field.type";

/** Default calling code when a number has no + / 00 prefix (Gobo CRM is DK-primary). */
export const DEFAULT_PHONE_COUNTRY_CODE = "45";

/**
 * Build a tel: href that mobile dialers accept.
 * Numbers without an international prefix get +{defaultCountryCode} (default +45).
 */
export const toTelHref = (
  value: string,
  defaultCountryCode: string = DEFAULT_PHONE_COUNTRY_CODE,
): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "tel:";
  }

  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return `tel:${trimmed}`;
  }

  if (hasPlus) {
    return `tel:+${digits}`;
  }

  // 0045... → +45...
  if (digits.startsWith("00") && digits.length > 2) {
    return `tel:+${digits.slice(2)}`;
  }

  // Already includes the default country code without + (e.g. 4533123456)
  if (
    digits.startsWith(defaultCountryCode) &&
    digits.length > defaultCountryCode.length + 5
  ) {
    return `tel:+${digits}`;
  }

  return `tel:+${defaultCountryCode}${digits}`;
};

const PhoneFieldImpl = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RecordType extends Record<string, any> = Record<string, any>,
>(
  inProps: PhoneFieldProps<RecordType>,
) => {
  const {
    className,
    empty,
    defaultValue,
    source,
    record,
    defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
    ...rest
  } = inProps;
  const value = useFieldValue({ defaultValue, source, record });
  const translate = useTranslate();

  if (value == null || value === "") {
    if (!empty) {
      return null;
    }

    return (
      <span className={className} {...rest}>
        {typeof empty === "string" ? translate(empty, { _: empty }) : empty}
      </span>
    );
  }

  const display = String(value);

  return (
    <a
      className={cn("underline hover:no-underline", className)}
      href={toTelHref(display, defaultCountryCode)}
      onClick={stopPropagation}
      {...rest}
    >
      {display}
    </a>
  );
};
PhoneFieldImpl.displayName = "PhoneFieldImpl";

/**
 * Displays a phone number as a clickable tel: link.
 */
export const PhoneField = genericMemo(PhoneFieldImpl);

export interface PhoneFieldProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RecordType extends Record<string, any> = Record<string, any>,
> extends FieldProps<RecordType>,
    AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Calling code without + when the stored number has no country prefix. */
  defaultCountryCode?: string;
}

const stopPropagation = (e: React.MouseEvent<HTMLAnchorElement>) =>
  e.stopPropagation();
